import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Plus, Edit, Trash2, X, Save, Search, Loader2, Download, Layers, Filter, ChevronDown, ChevronRight, ArrowUp, ArrowDown, Eye, RotateCcw } from 'lucide-react';
import { trailerService } from '../services/trailerService';
import { vehicleService } from '../services/vehicleService';
import { userService } from '../services/userService';
import { settingsService } from '../services/settingsService';
import { useAppAlert } from '../components/AppAlertProvider';
import { WorkiomTablePagination } from '../components/WorkiomTablePagination';
import { exportRowsToExcel } from '../utils/excelExport';
import { VEHICLE_TYPES } from '../constants/vehicleTypes';

interface Trailer {
  id: string;
  plate: string;
  trailerType: string;
  brand: string;
  model: string;
  year: number;
  capacity: string;
  volume: string;
  ownershipType: 'Filo' | 'Kiralık' | 'Spot';
  status: 'Aktif' | 'Bakımda' | 'Pasif';
  insuranceExpiry: string;
  inspectionExpiry: string;
  notes: string;
  linkedVehicleId?: string;
  linkedVehiclePlate?: string;
  assignmentHistory?: Array<{ id: string; vehicle_id: number; vehicle_plate?: string; assigned_at: string; unassigned_at?: string | null; is_active: number }>;
}

type TrailerTableView = {
  visibleColumns?: string[];
  columnFilters?: Record<string, string>;
  groupBy?: string;
  sortConfig?: { field: string; direction: 'asc' | 'desc' } | null;
};

const getStoredUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    return String(user?.id || 'anonymous');
  } catch {
    return 'anonymous';
  }
};

const normalizeDateInput = (value: unknown) => {
  if (!value) return '';
  const text = String(value).trim();
  const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const mapApiToUi = (item: any): Trailer => ({
  id: String(item.id),
  plate: item.plate || item.trailerPlate || '',
  trailerType: item.trailer_type || item.trailerType || '',
  brand: item.brand || '',
  model: item.model || '',
  year: Number(item.year || 0),
  capacity: item.capacity_kg != null && item.capacity_kg !== '' ? String(Number(item.capacity_kg) / 1000) : item.capacity || '',
  volume: item.capacity_m3 != null && item.capacity_m3 !== '' ? String(item.capacity_m3) : item.volume || '',
  ownershipType: (item.owner_type || item.ownerType) === 'rental' ? 'Kiralık' : (item.owner_type || item.ownerType) === 'external' ? 'Spot' : 'Filo',
  status: item.status === 'maintenance' ? 'Bakımda' : item.status === 'inactive' ? 'Pasif' : 'Aktif',
  insuranceExpiry: normalizeDateInput(item.insurance_expiry || item.insuranceExpiry),
  inspectionExpiry: normalizeDateInput(item.inspection_expiry || item.inspectionExpiry),
  notes: item.notes || '',
  linkedVehicleId: item.linked_vehicle_id ? String(item.linked_vehicle_id) : (item.linkedVehicleId ? String(item.linkedVehicleId) : ''),
  linkedVehiclePlate: item.linked_vehicle_plate || item.linkedVehiclePlate || '',
});

const mapUiToApi = (item: Partial<Trailer>, externalTransportAccountUserId?: string) => ({
  plate: item.plate,
  trailer_type: item.trailerType || null,
  brand: item.brand || null,
  model: item.model || null,
  year: item.year || null,
  capacity_kg: item.capacity ? Math.round(Number(item.capacity) * 1000) : null,
  capacity_m3: item.volume ? Number(item.volume) : null,
  owner_type: item.ownershipType === 'Kiralık' ? 'rental' : item.ownershipType === 'Spot' ? 'external' : 'own',
  status: item.status === 'Bakımda' ? 'maintenance' : item.status === 'Pasif' ? 'inactive' : 'active',
  insurance_expiry: item.insuranceExpiry || null,
  inspection_expiry: item.inspectionExpiry || null,
  notes: item.notes || null,
  linked_vehicle_id: item.linkedVehicleId || null,
  external_transport_account_user_id: externalTransportAccountUserId || undefined,
});

export function TrailerManagement() {
  const [searchParams] = useSearchParams();
  const { showAlert, showConfirm } = useAppAlert();
  const companyIdFilter = String(searchParams.get('companyId') || '').trim();
  const ownerTypeFilter = String(searchParams.get('ownerType') || '').trim();
  const isExternalOwnerView = ownerTypeFilter === 'external';
  const tableViewSettingKey = `trailer_management_table_view_${isExternalOwnerView ? 'external' : 'all'}_${companyIdFilter || 'all'}_${getStoredUserId()}`;
  const [data, setData] = useState<Trailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Trailer | null>(null);
  const [formData, setFormData] = useState<Partial<Trailer>>({});
  const [vehicles, setVehicles] = useState<Array<{ id: string; plate: string }>>([]);
  const [carrierCompanies, setCarrierCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [linkedVehicleSearch, setLinkedVehicleSearch] = useState('');
  const [linkedVehicleDropdownOpen, setLinkedVehicleDropdownOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);
  const [tableViewLoaded, setTableViewLoaded] = useState(false);

  const loadTrailers = async () => {
    setLoading(true);
    try {
      const response: any = await trailerService.getTrailers(companyIdFilter ? { externalTransportAccountUserId: companyIdFilter } : undefined);
      setData(Array.isArray(response?.data) ? response.data.map(mapApiToUi) : []);
    } catch (error) {
      console.error('Load trailers error:', error);
      showAlert({ tone: 'error', title: 'Yükleme Hatası', message: 'Dorseler yüklenemedi.' });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrailers();
    loadVehicles();
    loadCarrierCompanies();
  }, [companyIdFilter]);

  const loadVehicles = async () => {
    try {
      const response: any = await vehicleService.getVehicles(companyIdFilter ? { externalTransportAccountUserId: companyIdFilter } : undefined);
      const items = Array.isArray(response?.data) ? response.data : [];
      setVehicles(items.map((item: any) => ({ id: String(item.id), plate: item.cekici_plaka || item.plate || '-' })));
    } catch (error) {
      console.error('Load linked vehicles error:', error);
      setVehicles([]);
    }
  };

  const loadCarrierCompanies = async () => {
    try {
      const response: any = await userService.getUsers({ role: 'corporate-external-transport', status: 'active' }).catch(() => userService.getUsers());
      const items = Array.isArray(response?.data) ? response.data : [];
      setCarrierCompanies(items.filter((item: any) => item.role === 'corporate-external-transport').map((item: any) => ({ id: String(item.id), name: item.full_name || item.username || `Firma #${item.id}` })));
    } catch {
      setCarrierCompanies([]);
    }
  };

  const columns = [
    { key: 'plate', label: 'Dorse' },
    { key: 'trailerType', label: 'Dorse Tipi' },
    { key: 'brand', label: 'Marka' },
    { key: 'model', label: 'Model' },
    { key: 'year', label: 'Yıl' },
    { key: 'capacity', label: 'Kapasite (ton)' },
    { key: 'volume', label: 'Hacim' },
    { key: 'ownershipType', label: 'Mülkiyet' },
    { key: 'status', label: 'Durum' },
    { key: 'linkedVehiclePlate', label: 'Bağlı Çekici' },
  ];

  const resolvedColumns = useMemo(() => {
    const available = new Set(columns.map((column) => column.key));
    const visible = visibleColumnKeys.length > 0 ? visibleColumnKeys.filter((key) => available.has(key)) : columns.map((column) => column.key);
    return visible.map((key) => columns.find((column) => column.key === key)).filter(Boolean) as typeof columns;
  }, [visibleColumnKeys]);

  useEffect(() => {
    if (tableViewLoaded) return;
    settingsService.getMySettingValue<TrailerTableView | null>(tableViewSettingKey, null)
      .then((view) => {
        const available = new Set(columns.map((column) => column.key));
        if (view?.visibleColumns?.length) setVisibleColumnKeys(view.visibleColumns.filter((key) => available.has(key)));
        else setVisibleColumnKeys(columns.map((column) => column.key));
        if (view?.columnFilters) setColumnFilters(view.columnFilters);
        if (view?.groupBy && available.has(view.groupBy)) setGroupBy(view.groupBy);
        if (view?.sortConfig && available.has(view.sortConfig.field)) setSortConfig(view.sortConfig);
      })
      .catch(() => setVisibleColumnKeys(columns.map((column) => column.key)))
      .finally(() => setTableViewLoaded(true));
  }, [tableViewLoaded, tableViewSettingKey]);

  const saveTableView = async () => {
    await settingsService.saveMySetting(tableViewSettingKey, { visibleColumns: visibleColumnKeys, columnFilters, groupBy, sortConfig }, { category: 'ui', description: 'Trailer management table view' });
    showAlert({ tone: 'success', title: 'Görünüm Kaydedildi', message: 'Kolon, filtre, sıralama ve gruplama ayarları kaydedildi.' });
  };

  const resetTableView = () => {
    setVisibleColumnKeys(columns.map((column) => column.key));
    setColumnFilters({});
    setGroupBy('');
    setSortConfig(null);
  };

  const filtered = useMemo(() => data.filter((item) => {
    if (isExternalOwnerView && item.ownershipType !== 'Spot') return false;
    const matchesSearch = `${item.plate} ${item.trailerType} ${item.brand} ${item.model} ${item.linkedVehiclePlate || ''}`.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR'));
    const matchesStatus = !statusFilter || item.status === statusFilter;
    const matchesOwnership = !ownershipFilter || item.ownershipType === ownershipFilter;
    const matchesColumnFilters = Object.entries(columnFilters).every(([key, value]) => !value || String((item as any)[key] || '').toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR')));
    return matchesSearch && matchesStatus && matchesOwnership && matchesColumnFilters;
  }), [data, isExternalOwnerView, search, statusFilter, ownershipFilter, columnFilters]);

  const sorted = useMemo(() => {
    if (!sortConfig) return filtered;
    return [...filtered].sort((a: any, b: any) => {
      const left = String(a?.[sortConfig.field] ?? '').toLocaleLowerCase('tr-TR');
      const right = String(b?.[sortConfig.field] ?? '').toLocaleLowerCase('tr-TR');
      if (left < right) return sortConfig.direction === 'asc' ? -1 : 1;
      if (left > right) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortConfig]);

  const grouped = useMemo(() => {
    if (!groupBy) return { all: sorted };
    return sorted.reduce((acc: Record<string, Trailer[]>, item) => {
      const key = String((item as any)[groupBy] || '-');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [sorted, groupBy]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, ownershipFilter, groupBy, columnFilters, sortConfig]);

  const handleSort = (field: string) => {
    setSortConfig((current) => {
      if (!current || current.field !== field) return { field, direction: 'asc' };
      if (current.direction === 'asc') return { field, direction: 'desc' };
      return null;
    });
  };

  const renderSortableHeader = (label: string, field: string) => (
    <button type="button" onClick={() => handleSort(field)} className="inline-flex items-center gap-1 font-semibold text-gray-700">
      <span>{label}</span>
      {sortConfig?.field === field ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-600" /> : <ArrowDown className="w-3 h-3 text-blue-600" />) : <ArrowUp className="w-3 h-3 text-gray-300" />}
    </button>
  );

  const renderCell = (item: Trailer, key: string) => {
    if (key === 'status') {
      const colors = { 'Aktif': 'bg-green-100 text-green-800', 'Bakımda': 'bg-yellow-100 text-yellow-800', 'Pasif': 'bg-gray-100 text-gray-800' };
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.status]}`}>{item.status}</span>;
    }
    if (key === 'ownershipType') {
      const colors = { 'Filo': 'bg-blue-100 text-blue-800', 'Kiralık': 'bg-purple-100 text-purple-800', 'Spot': 'bg-orange-100 text-orange-800' };
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.ownershipType]}`}>{item.ownershipType}</span>;
    }
    return (item as any)[key] || '-';
  };

  const exportExcel = () => exportRowsToExcel(filtered as any[], `dorse-yonetimi-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'TrailerManagement' });

  const filteredVehicles = useMemo(() => {
    const query = linkedVehicleSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return vehicles;
    return vehicles.filter((vehicle) => String(vehicle.plate || '').toLocaleLowerCase('tr-TR').includes(query));
  }, [vehicles, linkedVehicleSearch]);

  const openForm = (item?: Trailer) => {
    if (item) {
      trailerService.getTrailer(item.id).then((response: any) => {
        const detailed = response?.data ? mapApiToUi(response.data) : item;
        detailed.assignmentHistory = Array.isArray(response?.data?.assignment_history) ? response.data.assignment_history.map((row: any) => ({ ...row, id: String(row.id) })) : [];
        setEditingItem(detailed);
        setFormData(detailed);
        setLinkedVehicleSearch(detailed.linkedVehiclePlate || '');
        setLinkedVehicleDropdownOpen(false);
        setIsFormOpen(true);
      }).catch(() => {
        setEditingItem(item);
        setFormData(item);
        setLinkedVehicleSearch(item.linkedVehiclePlate || '');
        setLinkedVehicleDropdownOpen(false);
        setIsFormOpen(true);
      });
      return;
    }
    setEditingItem(null);
    setFormData({ status: 'Aktif', ownershipType: ownerTypeFilter === 'external' || companyIdFilter ? 'Spot' : 'Filo', externalTransportAccountUserId: companyIdFilter || '' });
    setLinkedVehicleSearch('');
    setLinkedVehicleDropdownOpen(false);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setEditingItem(null);
    setFormData({});
    setLinkedVehicleSearch('');
    setLinkedVehicleDropdownOpen(false);
    setIsFormOpen(false);
  };

  const selectLinkedVehicle = (vehicle: { id: string; plate: string } | null) => {
    setFormData((current) => ({ ...current, linkedVehicleId: vehicle?.id || '' }));
    setLinkedVehicleSearch(vehicle?.plate || '');
    setLinkedVehicleDropdownOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingItem) await trailerService.updateTrailer(editingItem.id, mapUiToApi(formData, (formData as any).externalTransportAccountUserId || companyIdFilter));
      else await trailerService.createTrailer(mapUiToApi(formData, (formData as any).externalTransportAccountUserId || companyIdFilter));
      await loadTrailers();
      closeForm();
    } catch (error) {
      console.error('Save trailer error:', error);
      showAlert({ tone: 'error', title: 'Kayıt Hatası', message: 'Dorse kaydedilemedi.' });
    }
  };

  const handleDelete = async (id: string) => {
    if (await showConfirm({ title: 'Dorse Sil', message: 'Bu dorseyi silmek istediğinizden emin misiniz?', confirmText: 'Sil', tone: 'warning' })) {
      try {
        await trailerService.deleteTrailer(id);
        await loadTrailers();
      } catch (error) {
        console.error('Delete trailer error:', error);
        showAlert({ tone: 'error', title: 'Silme Hatası', message: 'Dorse silinemedi.' });
      }
    }
  };

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{ownerTypeFilter === 'external' ? 'Kurumsal Dış Nakliye Dorseleri' : 'Dorse Yönetimi'}</h1>
          <p className="text-gray-500 mt-1">{ownerTypeFilter === 'external' ? 'Kurumsal dış nakliye şirketlerine bağlı dorseleri yönetin' : 'Dorse kayıtlarını ve durumlarını yönetin'}</p>
        </div>
        <button onClick={() => openForm()} className="workiom-primary-button"><Plus className="w-5 h-5" /> Yeni Dorse</button>
      </div>

      <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2 sm:px-6 sm:py-6 xl:grid-cols-4">
        <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Toplam Dorse</div><div className="text-2xl font-bold text-gray-900 mt-1">{data.length}</div></div>
        <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Aktif</div><div className="text-2xl font-bold text-green-600 mt-1">{data.filter((x) => x.status === 'Aktif').length}</div></div>
        <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Bakımda</div><div className="text-2xl font-bold text-yellow-600 mt-1">{data.filter((x) => x.status === 'Bakımda').length}</div></div>
        <div className="bg-white rounded-lg shadow p-4"><div className="text-sm text-gray-500">Filo</div><div className="text-2xl font-bold text-blue-600 mt-1">{data.filter((x) => x.ownershipType === 'Filo').length}</div></div>
      </div>

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className="flex flex-wrap items-center gap-3 w-full">
          <div className="workiom-search max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="Dorse plaka, tip, marka ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="outline-none" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white sm:w-auto">
            <option value="">Tüm Durumlar</option>
            <option value="Aktif">Aktif</option>
            <option value="Bakımda">Bakımda</option>
            <option value="Pasif">Pasif</option>
          </select>
          <select value={ownershipFilter} onChange={(e) => setOwnershipFilter(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white sm:w-auto">
            <option value="">Tüm Mülkiyetler</option>
            <option value="Filo">Filo</option>
            <option value="Kiralık">Kiralık</option>
            <option value="Spot">Spot</option>
          </select>
          <button onClick={() => setShowColumnFilters((prev) => !prev)} className={`workiom-outline-button ${showColumnFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Filter className="w-4 h-4" /> Filtrele</button>
          <button onClick={() => setShowColumnsPanel((prev) => !prev)} className={`workiom-outline-button ${showColumnsPanel ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Eye className="w-4 h-4" /> Sütunlar</button>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white sm:w-auto">
            <option value="">Gruplama Yok</option>
            <option value="status">Durum</option>
            <option value="ownershipType">Mülkiyet</option>
            <option value="trailerType">Dorse Tipi</option>
            <option value="linkedVehiclePlate">Bağlı Çekici</option>
          </select>
          <button type="button" onClick={() => void saveTableView()} className="workiom-outline-button"><Save className="w-4 h-4" /> Görünümü Kaydet</button>
          <button type="button" onClick={resetTableView} className="workiom-outline-button"><RotateCcw className="w-4 h-4" /> Sıfırla</button>
          <button type="button" onClick={exportExcel} className="workiom-outline-button"><Download className="w-4 h-4" /> Excele Aktar</button>
          <div className="text-sm text-gray-500 inline-flex items-center gap-2"><Layers className="w-4 h-4" /> {filtered.length} kayıt</div>
        </div>
      </div>

      {showColumnsPanel && (
        <div className="mx-4 mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:mx-6">
          <div className="mb-3 text-sm font-semibold text-slate-700">Sütun göster/gizle ve sıralama</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {columns.map((column) => {
              const visible = visibleColumnKeys.includes(column.key);
              const index = visibleColumnKeys.indexOf(column.key);
              return (
                <div key={column.key} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2">
                  <label className="flex min-w-0 items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={visible} onChange={(event) => setVisibleColumnKeys((current) => event.target.checked ? [...current, column.key] : current.filter((key) => key !== column.key))} />
                    <span className="truncate">{column.label}</span>
                  </label>
                  <div className="flex gap-1">
                    <button type="button" disabled={!visible || index <= 0} onClick={() => setVisibleColumnKeys((current) => { const next = [...current]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next; })} className="rounded border border-slate-200 px-2 py-1 text-xs disabled:opacity-30">Sol</button>
                    <button type="button" disabled={!visible || index < 0 || index >= visibleColumnKeys.length - 1} onClick={() => setVisibleColumnKeys((current) => { const next = [...current]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next; })} className="rounded border border-slate-200 px-2 py-1 text-xs disabled:opacity-30">Sağ</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="workiom-grid-wrap border border-gray-200 bg-white visible-scrollbars" style={{ height: 550, overflow: 'auto' }}>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : (
          <table className="workiom-grid-table text-sm">
            <thead>
              <tr>
                <th className="workiom-grid-index text-left">#</th>
                {resolvedColumns.map((column) => <th key={column.key} className="text-left">{renderSortableHeader(column.label, column.key)}</th>)}
                <th className="workiom-grid-sticky-right text-left">İşlemler</th>
              </tr>
              {showColumnFilters && (
                <tr>
                  <th className="workiom-grid-index"></th>
                  {resolvedColumns.map((column) => <th key={`filter-${column.key}`}><input value={columnFilters[column.key] || ''} onChange={(e) => setColumnFilters((prev) => ({ ...prev, [column.key]: e.target.value }))} placeholder={`${column.label} filtre`} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" /></th>)}
                  <th className="workiom-grid-sticky-right"></th>
                </tr>
              )}
            </thead>
            <tbody>
              {Object.entries(grouped).flatMap(([groupKey, items]) => {
                const rows: any[] = [];
                if (groupBy) {
                  rows.push(<tr key={`group-${groupKey}`} className="bg-gray-100"><td colSpan={resolvedColumns.length + 2} className="px-3 py-2"><button type="button" onClick={() => setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">{collapsedGroups[groupKey] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}<span>{groupKey}</span><span className="text-gray-500">({items.length})</span></button></td></tr>);
                }
                if (!collapsedGroups[groupKey]) {
                  rows.push(...items.map((item, index) => (
                  <tr key={item.id} className="cursor-pointer" onDoubleClick={() => openForm(item)}>
                    <td className="workiom-grid-index">{index + 1}</td>
                    {resolvedColumns.map((column) => <td key={column.key}>{renderCell(item, column.key)}</td>)}
                    <td className="workiom-grid-sticky-right">
                      <div className="flex gap-2">
                        <button onClick={() => openForm(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                )));
                }
                return rows;
              })}
            </tbody>
          </table>
        )}
      </div>


      {isFormOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl bg-white sm:max-h-[90vh] sm:max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 p-4 sm:p-6">
              <h2 className="text-xl font-bold text-gray-900">{editingItem ? 'Dorse Düzenle' : 'Yeni Dorse'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Kurumsal Nakliye Firması</label><select value={(formData as any).externalTransportAccountUserId || companyIdFilter || ''} onChange={(e) => setFormData((current) => ({ ...current, externalTransportAccountUserId: e.target.value, ownershipType: e.target.value ? 'Spot' : (current.ownershipType || 'Filo') }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" disabled={!!companyIdFilter}><option value="">Bireysel / Bağımsız</option>{carrierCompanies.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Dorse Plaka *</label><input required value={formData.plate || ''} onChange={(e) => setFormData({ ...formData, plate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Dorse Tipi</label><select value={formData.trailerType || ''} onChange={(e) => setFormData({ ...formData, trailerType: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="">Seçiniz</option>{VEHICLE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}</select></div>
                <div className="relative" onBlur={() => window.setTimeout(() => setLinkedVehicleDropdownOpen(false), 120)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bağlı Çekici</label>
                  <input
                    value={linkedVehicleSearch}
                    onFocus={() => setLinkedVehicleDropdownOpen(true)}
                    onChange={(e) => {
                      setLinkedVehicleSearch(e.target.value);
                      setLinkedVehicleDropdownOpen(true);
                      setFormData((current) => ({ ...current, linkedVehicleId: '' }));
                    }}
                    placeholder="Çekici ara"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  {linkedVehicleDropdownOpen && (
                    <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectLinkedVehicle(null)}
                        className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
                      >
                        Seçimi Temizle
                      </button>
                      {filteredVehicles.length > 0 ? filteredVehicles.map((vehicle) => (
                        <button
                          key={vehicle.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectLinkedVehicle(vehicle)}
                          className={`block w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${String(formData.linkedVehicleId || '') === vehicle.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                        >
                          {vehicle.plate}
                        </button>
                      )) : (
                        <div className="px-3 py-2 text-sm text-gray-500">Eşleşen çekici bulunamadı</div>
                      )}
                    </div>
                  )}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Marka</label><input value={formData.brand || ''} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Model</label><input value={formData.model || ''} onChange={(e) => setFormData({ ...formData, model: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Yıl</label><input type="number" value={formData.year || ''} onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Kapasite (ton)</label><input type="number" step="0.01" value={formData.capacity || ''} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Hacim (m3)</label><input type="number" step="0.01" value={formData.volume || ''} onChange={(e) => setFormData({ ...formData, volume: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Mülkiyet</label><select value={formData.ownershipType || 'Filo'} onChange={(e) => setFormData({ ...formData, ownershipType: e.target.value as Trailer['ownershipType'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="Filo">Filo</option><option value="Kiralık">Kiralık</option><option value="Spot">Spot</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Durum</label><select value={formData.status || 'Aktif'} onChange={(e) => setFormData({ ...formData, status: e.target.value as Trailer['status'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="Aktif">Aktif</option><option value="Bakımda">Bakımda</option><option value="Pasif">Pasif</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Sigorta Bitiş</label><input type="date" value={formData.insuranceExpiry || ''} onChange={(e) => setFormData({ ...formData, insuranceExpiry: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Muayene Bitiş</label><input type="date" value={formData.inspectionExpiry || ''} onChange={(e) => setFormData({ ...formData, inspectionExpiry: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label><textarea rows={3} value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button type="button" onClick={closeForm} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">İptal</button>
                <button type="submit" className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"><Save className="w-4 h-4" /> Kaydet</button>
              </div>

              {editingItem && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Atama Geçmişi</h3>
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Çekici</th>
                          <th className="px-3 py-2 text-left">Bağlanma</th>
                          <th className="px-3 py-2 text-left">Ayrılma</th>
                          <th className="px-3 py-2 text-left">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(editingItem.assignmentHistory || []).length > 0 ? (editingItem.assignmentHistory || []).map((row) => (
                          <tr key={row.id} className="border-t border-gray-100">
                            <td className="px-3 py-2">{row.vehicle_plate || '-'}</td>
                            <td className="px-3 py-2">{row.assigned_at ? new Date(row.assigned_at).toLocaleString('tr-TR') : '-'}</td>
                            <td className="px-3 py-2">{row.unassigned_at ? new Date(row.unassigned_at).toLocaleString('tr-TR') : '-'}</td>
                            <td className="px-3 py-2">{row.is_active ? <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs">Aktif</span> : <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">Kapalı</span>}</td>
                          </tr>
                        )) : (
                          <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400">Kayıtlı atama geçmişi yok</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
